/**
 * DataSourceLabel Component
 *
 * A purple LabelIcon for different data sources/condition types.
 * Matches the PartConditionContainer icon from ~/code/ui
 */

import { LabelIcon, type IconProps } from '@customerio/pluma-components/react';

type IconName = IconProps['name'];

// Map of data source types to their icons
// Note: Icons match ~/code/ui/app/components/condition-editor-v2/nodes/*/view.gts
export const DATA_SOURCE_TYPE_ICON: Record<string, IconName> = {
  attribute: 'person', // attribute-change/view.gts
  event: 'trigger-event', // event/view.gts
  segment: 'segment', // segment/view.gts (default, can vary by segment.icon)
  device: 'mobile', // device-change/view.gts
  pageview: 'link', // pageview/view.gts uses 'url-parameters' which maps to 'link'
  screenview: 'modal-bottom', // screenview/view.gts
  form: 'form', // form-submit/view.gts
  message: 'email', // generic message
  email: 'email', // email-action/view.gts
  push: 'mobile', // action/view.gts (push maps to mobile)
  in_app: 'in-app', // action/view.gts
  sms: 'sms', // action/view.gts (twilio)
  slack: 'slack', // action/view.gts
  webhook: 'webhook', // action/view.gts
  inbound: 'sms', // inbound/view.gts
  opt_out: 'sms', // opt-out/view.gts
  relationship: 'connections', // relationship/view.gts
  subscription: 'newsletter', // subscription/view.gts
  object: 'object',
  value: 'database',
  unknown: 'error', // unknown/view.gts
};

export type DataSourceType =
  | 'attribute'
  | 'event'
  | 'segment'
  | 'device'
  | 'pageview'
  | 'screenview'
  | 'form'
  | 'message'
  | 'email'
  | 'push'
  | 'in_app'
  | 'sms'
  | 'slack'
  | 'webhook'
  | 'inbound'
  | 'opt_out'
  | 'relationship'
  | 'subscription'
  | 'object'
  | 'value'
  | 'unknown';

interface DataSourceLabelProps {
  /** The type of data source (determines the icon) */
  type: DataSourceType;
  /** Optional custom icon override */
  icon?: IconName;
  /** Additional CSS class */
  className?: string;
}

/**
 * Purple LabelIcon for displaying condition data sources.
 * Shows different icons based on the type (attribute, event, segment, etc.)
 * Uses LabelIcon with shape="square" and color="purple" to match ~/code/ui PartConditionContainer
 */
export function DataSourceLabel({ type, icon, className }: DataSourceLabelProps) {
  const iconName = icon ?? DATA_SOURCE_TYPE_ICON[type] ?? 'database';

  return <LabelIcon name={iconName} shape="square" color="purple" className={className} />;
}
